<template>
  <transition
      :name="name"
      :css="false"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
  >
    <div
        v-if="!collapsed"
        v-bind="$attrs"
        :style="{ transition }"
    >
      <slot></slot>
    </div>
  </transition>
</template>

<script>
export default {
  name: "CollapseTransition.vue",
  emits: ['leave-animation-done'],
  props: {
    duration: {
      type: Number,
      required: false,
      default: 300,
    },
    easing: {
      type: String,
      required: false,
      default: 'ease-in-out',
    },
    collapsed: {
      type: Boolean,
      required: false,
    }
  },
  data() {
    return {
      cachedStyles: null,
      name: 'collapse',
    }
  },
  methods: {
    enter(el, done) {
      // Because width and height may be 'auto',
      // first detect and cache the dimensions
      this.detectAndCacheDimensions(el)

      this.setClosedDimensions(el)
      // The order of applying styles is important:
      // - 1. Set styles for state before transition
      // - 2. Force repaint
      // - 3. Add transition style
      // - 4. Set styles for state after transition
      // If the order is not right and you open any 2nd level submenu
      // for the first time, the transition will not work.

      this.hideOverflow(el)
      this.forceRepaint(el)

      // Call done() when the transition ends
      // to trigger the @after-enter event.
      requestAnimationFrame(() => {
        this.setOpenedDimensions(el);
      });
      setTimeout(done, this.duration)
    },
    afterEnter(el) {
      // Clean up inline styles
      this.unsetOverflow(el)
      this.unsetDimensions(el)
      this.clearCachedDimensions()
    },
    beforeLeave() {
      this.leaveAnimationDone = false;
    },
    leave(el, done) {

      // For some reason, @leave triggered when starting
      // from open state on page load. So for safety,
      // check if the dimensions have been cached.
      this.detectAndCacheDimensions(el)

      this.setOpenedDimensions(el)
      // The order of applying styles is less important
      // than in the enter phase, as long as we repaint
      // before setting the closed dimensions.
      // But it is probably best to use the same
      // order as the enter phase.
      this.setOpenedDimensions(el)
      this.hideOverflow(el)
      this.forceRepaint(el)

      requestAnimationFrame(() => {
        this.setClosedDimensions(el);
      });

      // Call done() when the transition ends
      // to trigger the @after-leave event.
      // This will also cause v-show
      // to reapply 'display: none'.
      setTimeout(done, this.duration)
    },
    afterLeave(el) {
      // Clean up inline styles
      this.unsetOverflow(el)
      this.unsetDimensions(el)
      this.clearCachedDimensions()
    },
    detectAndCacheDimensions(el) {
      // Cache actual dimensions
      // only once to void invalid values when
      // triggering during a transition
      if (this.cachedStyles) return

      const visibility = el.style.visibility
      const display = el.style.display
      const height = el.style.height;
      // Trick to get the width and
      // height of a hidden element
      el.style.visibility = 'hidden'
      el.style.display = ''
      el.style.height = 'auto';

      this.cachedStyles = this.detectRelevantDimensions(el)

      // Restore any original styling
      el.style.visibility = visibility
      el.style.display = display
      el.style.height = height;
    },

    clearCachedDimensions() {
      this.cachedStyles = null
    },

    detectRelevantDimensions(el) {
      return {
        'height': el.offsetHeight + 'px',
      };
    },
    hideOverflow(el) {
      el.style.overflow = 'hidden'
    },

    unsetOverflow(el) {
      el.style.overflow = ''
    },
    setClosedDimensions(el) {
      el.style.height = '0';
    },
    setOpenedDimensions(el) {
      el.style.height = this.cachedStyles.height;
    },
    unsetDimensions(el) {
      el.style.height = 'auto';
    },
    forceRepaint(el) {
      // Force repaint to make sure the animation is triggered correctly.
      // Thanks: https://markus.oberlehner.net/blog/transition-to-height-auto-with-vue/
      getComputedStyle(el).height;
    },
  },
  computed: {
    transition() {
      return `height ${this.duration}ms ${this.easing}`;
    },
  }

};
</script>

<style scoped>

</style>
